import { isEmpty } from 'lodash';
import api from 'src/api';
import type TransactionDocument from 'src/models/transactions/items/transaction-document';
import type { DisplayDocument } from 'src/stores/ui-store';
import type { ZipFileOptions } from 'src/api/public/shared-packet';
import { sendMessage as sendMobileBridgeMessage } from 'src/utils/mobile-bridge';
import { downloadBlob } from './download-blob';
import {
  getDocumentBlob,
  GetDocumentBlobOptions,
  getDocumentQuery,
  getUrl,
} from './download-document';
import { isGlideMobileApp } from './browsers';

export interface DocumentArgs {
  doc?: DisplayDocument;
  transactionDocument?: TransactionDocument;
}

export interface GetDocumentQueryOptions {
  withUrl?: boolean;
}

export interface DownloadDocumentOptions extends GetDocumentBlobOptions {
  filename?: string;
}

export function canDocumentDownload(
  query: DisplayDocument | undefined,
  options: GetDocumentBlobOptions = {}
) {
  if (isEmpty(query)) {
    return false;
  }
  if (options.useProxy) {
    if (query.docToken || (query.flowId && query.documentId)) {
      return Boolean(options.useProxyV2);
    }
    return !isEmpty(getDocumentQuery({ doc: query }, { withUrl: false }));
  }
  return Boolean(query.url);
}

export async function downloadDocument(
  query: DisplayDocument,
  options: DownloadDocumentOptions = {}
) {
  const { filename = 'download.pdf', ...getDocumentBlobOptions } = options;
  if (isGlideMobileApp()) {
    const url = getUrl(query, getDocumentBlobOptions);
    if (url && (url.apiUrl || url.nonSecureUrl)) {
      const { apiUrl, nonSecureUrl } = url;
      // For mobile app, we use the mobile bridge for app to handle the download.
      sendMobileBridgeMessage({
        data: {
          event: 'downloadDocument',
          url: apiUrl,
          nonSecureUrl,
          filename,
        },
      });
      return;
    }
  } else {
    const blob = await getDocumentBlob(query, getDocumentBlobOptions);
    if (blob) {
      await downloadBlob(blob, filename);
      return;
    }
  }
  throw new Error('Could not find the source for the doc.');
}

export async function downloadMultipleTdvs(
  tdvIds: string[],
  filename: string,
  numberDocuments: boolean
) {
  if (isGlideMobileApp()) {
    const partialUrl = api.documents.getZipDownloadUrlByTdvIdsV2(
      tdvIds,
      filename,
      numberDocuments
    );
    sendMobileBridgeMessage({
      data: {
        event: 'downloadZip',
        url: `/api${partialUrl}`,
        filename,
      },
    });
    return;
  }
  await api.documents.downloadMultipleTdvs(tdvIds, filename, numberDocuments);
}

export async function downloadMultipleDocsAsZip(
  tdIds: string[],
  zipFilename: string,
  options: ZipFileOptions = {}
) {
  if (isGlideMobileApp()) {
    const partialUrl = api.documents.getZipDownloadUrlByTdIds(
      tdIds,
      zipFilename,
      options
    );
    sendMobileBridgeMessage({
      data: {
        event: 'downloadZip',
        url: `/api${partialUrl}`,
        filename: zipFilename,
      },
    });
    return;
  }
  await api.documents.downloadMultipleDocsAsZip(tdIds, zipFilename, options);
}

export async function downloadEnvelopeDocuments(
  envelopeId: string,
  edIds: string[],
  zipFilename: string,
  includeSigningCertificate = false,
  options: ZipFileOptions = {}
) {
  if (isGlideMobileApp()) {
    const partialUrl = api.documents.getZipEnvelopeDocumentsUrl(
      envelopeId,
      edIds,
      zipFilename,
      includeSigningCertificate,
      options
    );
    sendMobileBridgeMessage({
      data: {
        event: 'downloadZip',
        url: `/api${partialUrl}`,
        filename: zipFilename,
      },
    });
    return;
  }
  await api.documents.downloadEnvelopeDocuments(
    envelopeId,
    edIds,
    zipFilename,
    includeSigningCertificate,
    options
  );
}
